import useBodyScrollLock from '../hooks/useBodyScrollLock';
import React, { useState, useEffect, memo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import ReactPlayer from 'react-player';
import { FaArrowLeft, FaArrowRight } from 'react-icons/fa';
import { RiArrowRightLine } from "react-icons/ri"; // Asegúrate de que este icono exista
import { RiArrowRightSLine, RiArrowLeftSLine } from "react-icons/ri";

const FluidMediaModal = memo(({ show = false, onClose, mediaUrls = [], startIndex = 0 }) => {
    const [currentIndex, setCurrentIndex] = useState(startIndex);

    const goToPrevious = useCallback(() => {
        setCurrentIndex((prevIndex) => (prevIndex === 0 ? mediaUrls.length - 1 : prevIndex - 1));
    }, [mediaUrls.length]);

    const goToNext = useCallback(() => {
        setCurrentIndex((prevIndex) => (prevIndex === mediaUrls.length - 1 ? 0 : prevIndex + 1));
    }, [mediaUrls.length]);

    useBodyScrollLock(true);

    useEffect(() => {
        const handleKeys = (event) => {
            if (event.key === 'Escape') onClose();
            if (event.key === 'ArrowLeft') goToPrevious();
            if (event.key === 'ArrowRight') goToNext();
        };

        document.addEventListener('keydown', handleKeys);

        return () => {
            document.removeEventListener('keydown', handleKeys);
        };
    }, [onClose, goToNext, goToPrevious]);

    useEffect(() => {
        if (mediaUrls.length > 1) {
            const preloadNext = new Image();
            const preloadPrev = new Image();

            const nextIndex = (currentIndex + 1) % mediaUrls.length;
            const prevIndex = (currentIndex - 1 + mediaUrls.length) % mediaUrls.length;

            preloadNext.src = mediaUrls[nextIndex];
            preloadPrev.src = mediaUrls[prevIndex];
        }
    }, [currentIndex, mediaUrls]);

    const currentMediaUrl = mediaUrls[currentIndex];

    if (!show) return null;

    return ReactDOM.createPortal(
        <div
            className="fixed top-0 left-0 w-full h-full flex justify-center items-center bg-black bg-opacity-80"
            style={{ zIndex: 999999 }}
            onClick={onClose}
        >
            {mediaUrls.length > 1 && (
                <div className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-30 text-white px-4 py-2 rounded-full shadow-sm backdrop-blur-sm z-[99999] m-2">
                    <span className="font-medium">
                        {currentIndex + 1} / {mediaUrls.length}
                    </span>
                </div>
            )}

            <div className="relative w-full h-full max-w-4xl flex flex-col justify-center items-center p-4">
                <div
                    onClick={(e) => e.stopPropagation()}
                    className="w-full h-full flex justify-center items-center"
                >
                    <div className="relative w-full h-full sm:max-w-full sm:max-h-dvh sm:rounded-xl flex justify-center items-center">
                        {currentMediaUrl.endsWith('.mp4') || currentMediaUrl.endsWith('.webm') ? (
                            <ReactPlayer
                                url={currentMediaUrl}
                                controls={true}
                                playing={show}
                                width="100%"
                                height="100%"
                                className="w-full h-full"
                                config={{
                                    file: {
                                        attributes: {
                                            controlsList: 'nodownload',
                                            preload: 'metadata',
                                        },
                                    },
                                }}
                            />
                        ) : (
                            <img
                                src={currentMediaUrl}
                                alt={`Media ${currentIndex}`}
                                className="max-w-full max-h-full object-contain sm:rounded-xl"
                                loading="lazy"
                            />
                        )}
                    </div>
                </div>
            </div>

            <button
                className="absolute top-4 right-4 m-2 bg-black bg-opacity-30 text-white p-2 rounded-full"
                onClick={onClose}
            >
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-8 w-8"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                    />
                </svg>
            </button>

            {mediaUrls.length > 1 && (
                <>
                    {/* Botón para ir a la izquierda */}
                    <button
                        className="absolute top-1/2 left-4 transform -translate-y-1/2 m-1 bg-black bg-opacity-30 text-white p-1 rounded-full"
                        onClick={(event) => {
                            goToPrevious();
                            event.stopPropagation();
                        }}
                    >
                        <RiArrowLeftSLine className="h-6 w-6 md:h-14 md:w-14" />
                    </button>

                    {/* Botón para ir a la derecha */}
                    <button
                        className="absolute top-1/2 right-4 transform -translate-y-1/2 m-1 bg-black bg-opacity-30 text-white p-1 rounded-full"
                        onClick={(event) => {
                            goToNext();
                            event.stopPropagation();
                        }}
                    >
                        <RiArrowRightSLine className="h-6 w-6 md:h-14 md:w-14" />
                    </button>
                </>
            )}
        </div>,
        document.body
    );
});

FluidMediaModal.propTypes = {
    show: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    mediaUrls: PropTypes.arrayOf(PropTypes.string),
};

export default FluidMediaModal;
